import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { HugeiconsIcon } from "@hugeicons/react"
import { Copy01Icon } from "@hugeicons/core-free-icons"
import { ToolPage, ToolCard, ToolFieldRow, ToolTextField, ToolGuide } from '@/components/tools/ToolKit'

export default function UTMGenerator() {
  const [formData, setFormData] = useState({
    websiteURL: '',
    campaignID: '',
    campaignSource: '',
    campaignMedium: '',
    campaignName: '',
    campaignTerm: '',
    campaignContent: ''
  })

  const [generatedURL, setGeneratedURL] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    document.title = 'UTM Generator - Humblytics'
  }, [])

  // Common campaign sources and mediums for suggestions
  const commonSources = [
    'google',
    'facebook', 
    'twitter',
    'linkedin',
    'instagram',
    'email',
    'newsletter',
    'blog'
  ]

  const commonMediums = [
    'cpc',
    'social',
    'email',
    'display',
    'banner',
    'affiliate',
    'referral',
    'organic'
  ]

  function validateForm() {
    const newErrors: Record<string, string> = {}
    
    if (!formData.websiteURL) {
      newErrors.websiteURL = 'Website URL is required'
    } else if (!isValidURL(formData.websiteURL)) {
      newErrors.websiteURL = 'Enter a full URL, starting with https://'
    }
    
    if (!formData.campaignSource) newErrors.campaignSource = 'Campaign source is required'
    if (!formData.campaignMedium) newErrors.campaignMedium = 'Campaign medium is required'  
    if (!formData.campaignName) newErrors.campaignName = 'Campaign name is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function isValidURL(url: string) {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  function generateURL() {
    if (!validateForm()) return

    const url = new URL(formData.websiteURL)
    
    const utmParams = {
      utm_source: formData.campaignSource,
      utm_medium: formData.campaignMedium,
      utm_campaign: formData.campaignName,
      ...(formData.campaignID && { utm_id: formData.campaignID }),
      ...(formData.campaignTerm && { utm_term: formData.campaignTerm }),
      ...(formData.campaignContent && { utm_content: formData.campaignContent })
    }

    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) url.searchParams.append(key, value)
    })

    setGeneratedURL(url.toString())
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(generatedURL)
      toast.success('Link copied')
    } catch (err) {
      console.error('Failed to copy text: ', err)
      toast.error("Couldn't copy the link")
    }
  }

  function handleInput(field: string, value: string) {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const field = (key: keyof typeof formData) => ({
    value: formData[key],
    onChange: (v: string) => handleInput(key, v),
    error: errors[key],
  })

  const inputs = (
    <>
      <ToolCard title="UTM parameters" tag="Required">
        <ToolTextField id="url" type="url" label="Website URL" placeholder="https://yourwebsite.com" {...field('websiteURL')} />
        <ToolFieldRow>
          <ToolTextField id="source" label="Campaign source" hint="utm_source" placeholder="e.g. google, newsletter" list="sourceSuggestions" {...field('campaignSource')} />
          <ToolTextField id="medium" label="Campaign medium" hint="utm_medium" placeholder="e.g. cpc, email, social" list="mediumSuggestions" {...field('campaignMedium')} />
          <ToolTextField id="campaign" label="Campaign name" hint="utm_campaign" placeholder="e.g. summer_sale" {...field('campaignName')} />
        </ToolFieldRow>
        <datalist id="sourceSuggestions">
          {commonSources.map((source) => <option key={source} value={source} />)}
        </datalist>
        <datalist id="mediumSuggestions">
          {commonMediums.map((medium) => <option key={medium} value={medium} />)}
        </datalist>
      </ToolCard>
      <ToolCard title="Optional parameters" tag="Optional">
        <ToolFieldRow>
          <ToolTextField id="term" label="Campaign term" hint="utm_term" placeholder="Keywords for paid search" {...field('campaignTerm')} />
          <ToolTextField id="content" label="Campaign content" hint="utm_content" placeholder="Differentiate ads, e.g. logolink" {...field('campaignContent')} />
        </ToolFieldRow>
      </ToolCard>
      {generatedURL && (
        <ToolCard title="Your link" description="Use it in your campaign; visits show up under Traffic sources.">
          <div className="flex gap-2">
            <Input
              id="generated-url"
              aria-label="Generated UTM URL"
              readOnly
              value={generatedURL}
              onFocus={(e) => e.currentTarget.select()}
              className="h-9 flex-1 rounded-lg bg-canvas px-3 font-mono text-xs"
            />
            <Button variant="outline" onClick={copyToClipboard} className="h-9 gap-1.5 rounded-lg px-3 text-[13px]">
              <HugeiconsIcon icon={Copy01Icon} size={14} />
              Copy
            </Button>
          </div>
        </ToolCard>
      )}
    </>
  )

  return (
    <ToolPage
      title="UTM generator"
      description="Build campaign links with UTM parameters."
      inputs={inputs}
      footer={
        <Button onClick={generateURL} className="h-[34px] rounded-lg px-3.5 text-[13px] shadow-none">
          Generate UTM URL
        </Button>
      }
      aside={
        <ToolGuide
          title="Quick guide"
          steps={[
            'Enter your website URL and campaign details',
            'Fill in the source, medium and campaign name',
            'Add term and content for more detailed tracking',
            'Copy the generated URL and use it in your campaigns',
          ]}
        />
      }
    />
  )
}
